import { Box, Pagination, PaginationProps, PaginationRenderItemParams } from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '~/assets/icons';
import styles from './BasePagination.module.scss';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';

const cx = classNames.bind(styles);

type BasePaginationProps = PaginationProps;

const BasePagination = (props: BasePaginationProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { page, onChange, ...restProps } = props;

  useEffect(() => {
    setCurrentPage(page !== undefined ? page : 1);
  }, [page]);

  const { t } = useTranslation('admin/common');
  return (
    <Pagination
      className={cx('pagination-container')}
      color='primary'
      variant='outlined'
      shape='rounded'
      page={currentPage}
      onChange={(event: React.ChangeEvent<any>, newPage: number) => {
        setCurrentPage(newPage);
        if (onChange) {
          onChange(event, newPage);
        }
      }}
      renderItem={(params: PaginationRenderItemParams) => {
        if (params.type === 'previous') {
          return (
            <Box
              className={cx('previous-button', { 'cursor-pointer': !params.disabled })}
              onClick={!params.disabled ? params.onClick : undefined}
            >
              <ArrowLeftIcon
                className={cx('w-[12px] h-[12px]', { 'opacity-[.2]': params.disabled })}
                stroke='#3A3F51'
              />
              <Box className={cx('text-label', { 'opacity-[.2]': params.disabled })}>{t('previous')}</Box>
            </Box>
          );
        }
        if (params.type === 'next') {
          return (
            <Box
              className={cx('next-button', { 'cursor-pointer': !params.disabled })}
              onClick={!params.disabled ? params.onClick : undefined}
            >
              <Box className={cx('text-label', { 'opacity-[.2]': params.disabled })}>{t('next')}</Box>
              <ArrowRightIcon
                className={cx('w-[12px] h-[12px]', { 'opacity-[.2]': params.disabled })}
                stroke='#3A3F51'
              />
            </Box>
          );
        }
        if (params.type === 'page') {
          return (
            <Box
              className={cx(
                'page-button',
                { 'bg-[#007AFC] border-none': params.selected },
                { 'border-[1px] border-[#DBDBDB]': !params.selected }
              )}
              onClick={params.onClick}
            >
              <Box
                className={cx(
                  'text-label',
                  { 'text-[#FFFFFF]': params.selected },
                  { 'text-[#3A3F51]': !params.selected }
                )}
              >
                {params.page}
              </Box>
            </Box>
          );
        }
        if (params.type === 'start-ellipsis' || params.type === 'end-ellipsis') {
          return (
            <Box className='flex w-[32px] h-[32px] border-[1px] border-[#DBDBDB] rounded-[6px] items-center justify-center ml-[10px]'>
              <Box className='text-[14px]/[16.8]px font-normal text-[#3A3F51]'>...</Box>
            </Box>
          );
        }
        return null;
      }}
      {...restProps}
    />
  );
};

export default memo(BasePagination);
